/**
 * Admin V3 — Financial / Billing / Invoices — top-level page
 *
 * Mounted at: /admin-v3/billing/invoices (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Partner invoice ledger + draft generation.
 * Source: GET /admin/billing/invoices (server-side partner_id + status filters).
 *
 * SCOPE:
 *   - GET /admin/billing/invoices (list/search/filter; per-row expansion)
 *   - Row click → /admin-v3/billing/invoices/:id (BillingInvoiceDetailPage)
 *   - Tier 1 (fiscal UI): permission-gated "Générer une facture" affordance →
 *     POST /admin/billing/invoices/generate. This creates a DRAFT only (no legal
 *     number consumed); issuing is a separate explicit action on the detail page.
 *
 * RBAC: the generate affordance is hidden unless
 * usePermissions().can('admin:partner:update'); the backend @rbac_permission is
 * the authoritative gate. Issue / void / payment live on the detail page.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Plus, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, currency } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';
import BillingInvoicesSearchBar from './BillingInvoicesSearchBar';
import BillingInvoicesTable from './BillingInvoicesTable';

const PERM_UPDATE = 'admin:partner:update';

// HARM-STOP (incident 2026-06-12): the manual "Générer un brouillon" affordance
// is disabled. It called a legacy generator that consumed a legal number at
// generation and auto-issued empty/paid invoices (defect F-2026-000001).
// Invoice creation must flow through the Devis → Bon de commande → Booking
// pipeline. Re-enable only after the corrected lifecycle is deployed + ratified.
const GENERATE_ENABLED = false;

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 50;

const EMPTY_GEN = { partner_id: '', period_start: '', period_end: '', service_type: 'transport', payment_terms_days: 30 };

const BillingInvoicesPage = () => {
  const locale = DEFAULT_LOCALE;
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();
  const canCreate = can(PERM_UPDATE);

  const [status, setStatus] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [debouncedPartnerId, setDebouncedPartnerId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState(EMPTY_GEN);
  const generateM = useAdminMutation('/admin/billing/invoices/generate', 'post');

  const setGenField = (k) => (e) => setGen((g) => ({ ...g, [k]: e.target.value }));
  const genValid = gen.partner_id.trim() && gen.period_start && gen.period_end;

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedPartnerId(partnerId.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [partnerId]);

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (debouncedPartnerId) queryParams.partner_id = debouncedPartnerId;
  if (status)             queryParams.status     = status;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/billing/invoices',
    queryParams
  );

  const handleGenerate = () => {
    generateM.mutate(
      { ...gen, payment_terms_days: Number(gen.payment_terms_days) || 30 },
      {
        onSuccess: () => { showSuccess(t('billing.generate.ok', locale)); setGenOpen(false); setGen(EMPTY_GEN); refetch(); },
        onError: (err) => showError(err?.response?.data?.detail || t('billing.generate.error', locale)),
      }
    );
  };

  const allInvoices = Array.isArray(data?.invoices) ? data.invoices : [];

  // Client-side search by invoice_number (case-insensitive)
  const invoices = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allInvoices;
    return allInvoices.filter((inv) =>
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.partner_name || '').toLowerCase().includes(q)
    );
  }, [allInvoices, debouncedSearch]);

  // Summary: total amount due (sum of amount_due) across loaded page
  const summary = useMemo(() => {
    const totalDue = allInvoices.reduce((acc, inv) => acc + (Number(inv.amount_due) || 0), 0);
    return {
      pageCount: invoices.length,
      totalDue,
    };
  }, [allInvoices, invoices]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(debouncedPartnerId) || Boolean(status);
  const isAtBackendCap = allInvoices.length === DEFAULT_LIMIT;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('billing.invoices.title', locale)}
      icon={FileText}
      iconClass="bg-blue-100 text-blue-600"
      action={(canCreate && GENERATE_ENABLED) ? (
        <Button variant="default" size="sm" onClick={() => setGenOpen((v) => !v)} data-testid="adminv3-billing-generate-toggle">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{t('billing.invoices.action.generate', locale)}
        </Button>
      ) : null}
    >
      <div className="space-y-4">
        {genOpen && canCreate && GENERATE_ENABLED && (
          <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-3" data-testid="adminv3-billing-generate-form">
            <p className="text-[12px] text-slate-500">{t('billing.generate.hint', locale)}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.generate.partnerId', locale)}</label>
                <input value={gen.partner_id} onChange={setGenField('partner_id')}
                       className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-billing-generate-partner" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.generate.serviceType', locale)}</label>
                <div className="relative">
                  <select value={gen.service_type} onChange={setGenField('service_type')}
                          className="w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 pr-9 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-billing-generate-service">
                    <option value="transport">{t('billing.generate.serviceType.transport', locale)}</option>
                    <option value="standard">{t('billing.generate.serviceType.standard', locale)}</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                </div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.generate.periodStart', locale)}</label>
                <input type="date" value={gen.period_start} onChange={setGenField('period_start')}
                       className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-billing-generate-start" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.generate.periodEnd', locale)}</label>
                <input type="date" value={gen.period_end} onChange={setGenField('period_end')}
                       className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-billing-generate-end" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-slate-600">{t('billing.generate.paymentTerms', locale)}</label>
                <input type="number" min="0" value={gen.payment_terms_days} onChange={setGenField('payment_terms_days')}
                       className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" data-testid="adminv3-billing-generate-terms" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="default" size="sm" onClick={handleGenerate} disabled={generateM.loading || !genValid}
                      data-testid="adminv3-billing-generate-submit">
                {generateM.loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {t('billing.generate.submit', locale)}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setGenOpen(false)}>{t('billing.invoiceDetail.cancel', locale)}</Button>
            </div>
          </div>
        )}

        <BillingInvoicesSearchBar
          status={status}
          onStatusChange={setStatus}
          partnerId={partnerId}
          onPartnerIdChange={setPartnerId}
          search={search}
          onSearchChange={setSearch}
          resultsCount={invoices.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        {data && (
          <div
            className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1"
            data-testid="adminv3-billing-invoices-summary"
          >
            <span>{t('billing.invoices.summary.total', locale).replace('{n}', String(summary.pageCount))}</span>
            {summary.totalDue > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-rose-600 font-medium">
                  {t('billing.invoices.summary.unpaid', locale).replace('{amount}', currency(summary.totalDue))}
                </span>
              </>
            )}
          </div>
        )}

        <BillingInvoicesTable
          invoices={invoices}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-billing-invoices-cap-hint"
          >
            {t('billing.invoices.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default BillingInvoicesPage;
